// Formatters PT-BR. Espelha apps/web/js/utils.js (Utils.format*).
// Centralizados aqui pra reuso entre pages e tabelas.
//
// Todos aceitam null/undefined com fallback "-" pra evitar crash quando
// payload do backend tem campos opcionais (ex: capacidadeTanque, autonomia,
// kmAtual etc costumam vir null em veiculos antigos importados).

const FALLBACK = '-';

export function formatBRL(value: number | null | undefined, options?: { semSimbolo?: boolean }): string {
  if (value == null || !Number.isFinite(value)) return FALLBACK;
  const formatted = value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return options?.semSimbolo ? formatted.replace('R$', '').trim() : formatted;
}

export function formatNumber(value: number | null | undefined, decimals = 0): string {
  if (value == null || !Number.isFinite(value)) return FALLBACK;
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function formatPercent(value: number | null | undefined, decimals = 1): string {
  if (value == null || !Number.isFinite(value)) return FALLBACK;
  return `${formatNumber(value, decimals)}%`;
}

export function formatLitros(value: number | null | undefined, decimals = 1): string {
  if (value == null || !Number.isFinite(value)) return FALLBACK;
  return `${formatNumber(value, decimals)} L`;
}

// --- Datas ---------------------------------------------------------------
//
// Regra canonica: valor "date-only" (YYYY-MM-DD, ex: inicio/fim de
// agendamento, dataEmissao/dataVencimento de fatura) representa uma data
// civil, nao um instante. Passar essa string por `new Date()` faz o JS
// parsear como midnight UTC; em America/Sao_Paulo (UTC-3) o toLocaleDateString
// devolve o dia ANTERIOR (bug de off-by-one). Por isso date-only e formatado
// direto pelos componentes da string, sem conversao de fuso.
//
// Valor datetime completo (aplicadoEm, finalizadoEm, lastLoginAt) continua
// passando por `new Date()` + Intl: ali a conversao pro fuso local e correta.

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const TZ_SAO_PAULO = 'America/Sao_Paulo';

/** Resultado de classificar uma string como date-only.
 *  `nao-date-only`: nao tem a forma YYYY-MM-DD, segue pro caminho datetime.
 *  `invalida`: tem a forma mas nao existe no calendario (2027-02-29).
 *  Esses dois casos precisam ser distintos: cair no `new Date()` com uma
 *  date-only invalida NAO da Invalid Date, da rollover silencioso
 *  (`new Date('2027-02-29')` = 01/03, exibido como 28/02 em UTC-3). */
type DateOnlyParse =
  | { kind: 'ok'; ano: string; mes: string; dia: string }
  | { kind: 'invalida' }
  | { kind: 'nao-date-only' };

function parseDateOnly(value: string): DateOnlyParse {
  const m = DATE_ONLY_RE.exec(value);
  if (!m) return { kind: 'nao-date-only' };
  const [, ano, mes, dia] = m;
  // Valida existencia da data em UTC (sem fuso envolvido) pra rejeitar
  // 2026-13-01 / 2027-02-29 em vez de imprimir lixo.
  const probe = new Date(Date.UTC(Number(ano), Number(mes) - 1, Number(dia)));
  if (
    probe.getUTCFullYear() !== Number(ano) ||
    probe.getUTCMonth() + 1 !== Number(mes) ||
    probe.getUTCDate() !== Number(dia)
  ) {
    return { kind: 'invalida' };
  }
  return { kind: 'ok', ano, mes, dia };
}

/** Formata date-only, ou devolve null se a string nao for date-only.
 *  Date-only invalida retorna o FALLBACK, nunca cai no `new Date()`. */
function formatDateOnly(value: string): string | null {
  const p = parseDateOnly(value);
  if (p.kind === 'nao-date-only') return null;
  if (p.kind === 'invalida') return FALLBACK;
  return `${p.dia}/${p.mes}/${p.ano}`;
}

export function formatDate(value: string | Date | null | undefined): string {
  if (value == null || value === '') return FALLBACK;
  if (typeof value === 'string') {
    const only = formatDateOnly(value.trim());
    if (only !== null) return only;
  }
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return FALLBACK;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function formatDateTime(value: string | Date | null | undefined): string {
  if (value == null || value === '') return FALLBACK;
  // Date-only nao carrega hora. Inventar "00:00" seria falso e reintroduziria
  // o shift de fuso, entao degradamos pra data pura (mesma saida do
  // formatDate). Quem precisa de hora deve receber datetime completo da API.
  if (typeof value === 'string') {
    const only = formatDateOnly(value.trim());
    if (only !== null) return only;
  }
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return FALLBACK;
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Coluna Postgres DATE (ex: crd_tra_data) chega serializada pelo Prisma como
// meia-noite UTC: "2026-10-11T00:00:00.000Z". Passar isso por `new Date` e
// formatar no fuso do browser mostra o dia anterior no Brasil (10/10/2026).
// A data e civil, sem fuso: usa so o prefixo YYYY-MM-DD.
const PREFIXO_DATA = /^(\d{4}-\d{2}-\d{2})(?:T|$)/;

/** Data de coluna DATE como "dd/mm/aaaa", sem conversao de fuso. */
export function formatDataCivil(value: string | Date | null | undefined): string {
  if (value == null || value === '') return FALLBACK;
  const texto = value instanceof Date ? value.toISOString() : value.trim();
  const m = PREFIXO_DATA.exec(texto);
  return m ? formatDate(m[1]) : formatDate(value);
}

// Coluna Postgres TIME (ex: crd_tra_hora) chega serializada pelo Prisma como
// ISO na data epoch: "1970-01-01T14:23:05.000Z". A hora ja e a hora civil
// gravada; o "Z" e artefato da serializacao, entao NAO converte fuso. Tambem
// aceita "14:23" e "14:23:05" puros.
const HORA_ISO_EPOCH = /^\d{4}-\d{2}-\d{2}T(\d{2}):(\d{2})/;
const HORA_PURA = /^(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/;

/** Hora de coluna TIME como "HH:mm". Valor que nao e hora vira fallback. */
export function formatHora(value: string | Date | null | undefined): string {
  if (value == null || value === '') return FALLBACK;
  const texto = value instanceof Date ? value.toISOString() : value.trim();
  const m = HORA_ISO_EPOCH.exec(texto) ?? HORA_PURA.exec(texto);
  if (!m) return FALLBACK;
  return `${m[1]}:${m[2]}`;
}

/** Data civil de hoje em America/Sao_Paulo no formato YYYY-MM-DD.
 *  Use isto pra `min`/`max` de `<input type="date">`.
 *  NAO use `new Date().toISOString().slice(0,10)`: aquilo devolve a data em
 *  UTC e depois das 21h em Brasilia ja aponta pro dia seguinte.
 *  Locale en-CA emite exatamente YYYY-MM-DD. */
export function todayDateOnly(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ_SAO_PAULO,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/** Normaliza valor vindo da API para o `value` de `<input type="date">`.
 *  Aceita date-only (repassa) e datetime ISO (corta a porcao de data da
 *  string, sem `new Date()`, pra nao deslocar o dia). */
export function toDateInputValue(value: string | null | undefined): string {
  if (!value) return '';
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(value.trim());
  return m ? m[1] : '';
}

export function variation(current: number, previous: number): number {
  if (previous === 0) return 0;
  return ((current - previous) / previous) * 100;
}

/** Formata CNPJ bruto (14 digitos) no padrao ##.###.###/####-##.
 *  Retorna null se o input for nulo/vazio ou nao tiver 14 digitos. */
export function formatCnpj(cnpj: string | null | undefined): string | null {
  if (!cnpj) return null;
  const d = cnpj.replace(/\D/g, '');
  if (d.length !== 14) return cnpj;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}
